import React from 'react';
import { toast } from 'sonner';
import { Sun, Moon, Monitor, UtensilsCrossed, UserCircle2, Clock } from 'lucide-react';
import { useGet, apiPatch } from '../../hooks/useApi.js';
import { useTheme } from '../../hooks/useTheme.js';
import type { Theme } from '../../lib/theme.js';

interface MeSettings {
  email: string;
  name: string;
  isTechnician: boolean;
  technicianName: string | null;
  lunchStart: string;
  lunchEnd: string;
  timezone: string;
}

const TIME_OPTIONS = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      out.push(String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0'));
    }
  }
  return out;
})();

export function UserSettingsPage() {
  const { data, loading, refetch } = useGet<MeSettings>('/api/v1/me/settings');
  const { theme, setTheme } = useTheme();
  const [lunchStart, setLunchStart] = React.useState('');
  const [lunchEnd, setLunchEnd] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (data && !initialized.current) {
      setLunchStart(data.lunchStart);
      setLunchEnd(data.lunchEnd);
      initialized.current = true;
    }
  }, [data]);

  async function saveLunch() {
    if (!lunchStart || !lunchEnd || lunchStart >= lunchEnd) {
      toast.error('Lunch start must be before lunch end');
      return;
    }
    setSaving(true);
    try {
      await apiPatch('/api/v1/me/settings', { lunchStart, lunchEnd });
      toast.success('Lunch set to ' + lunchStart + '–' + lunchEnd + ' — calendar blocks will update within the hour');
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save lunch window');
    } finally {
      setSaving(false);
    }
  }

  const themes: Array<{ value: Theme; label: string; icon: React.ReactNode; hint: string }> = [
    { value: 'light', label: 'Light', icon: <Sun className="h-5 w-5" />, hint: 'Bright surfaces' },
    { value: 'dark', label: 'Dark', icon: <Moon className="h-5 w-5" />, hint: 'Easy on the eyes' },
    { value: 'system', label: 'System', icon: <Monitor className="h-5 w-5" />, hint: 'Match your device' },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <UserCircle2 className="h-8 w-8 text-brand-600" />
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">Settings</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))]">{loading ? 'Loading…' : (data?.name ?? '') + (data?.email ? ' · ' + data.email : '')}</p>
        </div>
      </div>

      {/* Appearance */}
      <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 space-y-4">
        <h2 className="text-base font-semibold text-[hsl(var(--content))]">Appearance</h2>
        <p className="text-sm text-[hsl(var(--content-subtle))]">Choose how TechSys Connect looks on this device.</p>
        <div className="grid grid-cols-3 gap-3">
          {themes.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTheme(t.value)}
              className={'rounded-xl border p-4 flex flex-col items-center gap-2 transition-colors ' + (theme === t.value
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
                : 'border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]')}
              aria-pressed={theme === t.value}
            >
              {t.icon}
              <span className="text-sm font-semibold">{t.label}</span>
              <span className="text-[10px] text-[hsl(var(--content-placeholder))]">{t.hint}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Lunch block */}
      <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 space-y-4">
        <div className="flex items-center gap-2">
          <UtensilsCrossed className="h-5 w-5 text-brand-600" />
          <h2 className="text-base font-semibold text-[hsl(var(--content))]">Lunch Block</h2>
        </div>
        {data && !data.isTechnician ? (
          <p className="text-sm text-[hsl(var(--content-subtle))] rounded-xl bg-[hsl(var(--surface-subtle))] p-3">
            No technician record is linked to your account, so there's no calendar lunch block to set. An admin can link one via People → Technicians.
          </p>
        ) : (
          <>
            <p className="text-sm text-[hsl(var(--content-subtle))]">
              Your recurring lunch window on the dispatch calendar{data?.timezone ? ' (' + data.timezone + ')' : ''}. Changing it re-books your future lunch blocks automatically.
            </p>
            <div className="flex items-end gap-3">
              <label className="flex-1">
                <span className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--content-subtle))]"><Clock className="h-3 w-3" /> Start</span>
                <select value={lunchStart} onChange={(e) => setLunchStart(e.target.value)} disabled={loading || saving}
                  className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500">
                  {TIME_OPTIONS.map((t) => <option key={t} value={t}>{fmtTime(t)}</option>)}
                </select>
              </label>
              <span className="pb-2.5 text-sm text-[hsl(var(--content-subtle))]">to</span>
              <label className="flex-1">
                <span className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--content-subtle))]"><Clock className="h-3 w-3" /> End</span>
                <select value={lunchEnd} onChange={(e) => setLunchEnd(e.target.value)} disabled={loading || saving}
                  className="mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-3 py-2 text-sm text-[hsl(var(--content))] outline-none focus:ring-2 focus:ring-brand-500">
                  {TIME_OPTIONS.map((t) => <option key={t} value={t}>{fmtTime(t)}</option>)}
                </select>
              </label>
              <button type="button" onClick={saveLunch} disabled={loading || saving || lunchStart >= lunchEnd}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function fmtTime(hhmm: string): string {
  const parts = hhmm.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return h12 + ':' + String(m).padStart(2, '0') + ' ' + ampm;
}
